/* Progressive enhancement. Every behaviour here has a working no-JS path underneath it:
   the gallery renders its first image, the stepper is a number input, the variant picker
   is a radio group inside a real form, and add-to-cart is a form POST. Nothing below is
   required to buy a product — it only makes doing so nicer.

   No framework, no build step. Delegated listeners on document so anything the Section
   Rendering API swaps in keeps working without re-binding. */

(() => {
  'use strict';

  const money = (cents, format) => {
    // Shopify's money format string, the subset that actually appears in practice.
    const n = (cents / 100).toFixed(2);
    const withCommas = n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, withCommas);
  };

  /* ---------------------------------------------------------------- Gallery */

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-media-target]');
    if (!thumb) return;

    const gallery = thumb.closest('[data-gallery]');
    if (!gallery) return;

    const id = thumb.getAttribute('data-media-target');

    gallery.querySelectorAll('[data-media-id]').forEach((item) => {
      const match = item.getAttribute('data-media-id') === id;
      item.hidden = !match;
      item.classList.toggle('is-active', match);
    });

    gallery.querySelectorAll('[data-media-target]').forEach((t) => {
      const match = t === thumb;
      t.classList.toggle('is-active', match);
      // aria-current, not aria-selected: these are buttons, not tabs.
      if (match) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  });

  /* ---------------------------------------------------------- Quantity step */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;

    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('.qty__input');
    if (!input) return;

    const step = parseInt(btn.getAttribute('data-qty-step'), 10);
    const min = parseInt(wrap.getAttribute('data-qty-min') || '1', 10);
    const maxAttr = wrap.getAttribute('data-qty-max');
    const max = maxAttr ? parseInt(maxAttr, 10) : Infinity;

    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + step));
    if (next === parseInt(input.value, 10)) return;

    input.value = next;
    // Fires change so a cart line listener can pick it up; the button click alone
    // would not.
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncQtyBounds(wrap);
  });

  document.addEventListener('input', (e) => {
    if (e.target.classList && e.target.classList.contains('qty__input')) {
      syncQtyBounds(e.target.closest('[data-qty]'));
    }
  });

  function syncQtyBounds(wrap) {
    if (!wrap) return;
    const input = wrap.querySelector('.qty__input');
    const min = parseInt(wrap.getAttribute('data-qty-min') || '1', 10);
    const maxAttr = wrap.getAttribute('data-qty-max');
    const max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    const val = parseInt(input.value, 10) || min;

    const dec = wrap.querySelector('[data-qty-step="-1"]');
    const inc = wrap.querySelector('[data-qty-step="1"]');
    if (dec) dec.disabled = val <= min;
    if (inc) inc.disabled = val >= max;
  }

  document.querySelectorAll('[data-qty]').forEach(syncQtyBounds);

  /* ------------------------------------------------------------ Variants */

  // The full variant list is emitted as JSON by the product section so option changes
  // resolve without a round trip.
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !e.target.name || e.target.name.indexOf('options[') !== 0) return;

    const dataEl = document.querySelector('[data-variant-json]');
    if (!dataEl) return;

    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (err) {
      return; // malformed payload: leave the no-JS submit path intact
    }

    const chosen = Array.from(form.querySelectorAll('input[name^="options["]:checked'))
      .map((i) => i.value);

    const match = variants.find(
      (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
    );
    if (!match) return;

    const idField = form.querySelector('[data-variant-id]');
    if (idField) idField.value = match.id;

    // Keep the URL shareable — same behaviour as Shopify's own themes.
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    updatePrice(match, dataEl);
    updateAvailability(match, form);
  });

  function updatePrice(variant, dataEl) {
    const el = document.querySelector('.price--pdp');
    if (!el) return;
    const fmt = dataEl.getAttribute('data-money-format');
    const amount = el.querySelector('.price__amount');
    if (amount) amount.textContent = money(variant.price, fmt);

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    el.classList.toggle('price--sale', !!onSale);

    let was = el.querySelector('.price__was');
    if (onSale) {
      if (!was) {
        was = document.createElement('s');
        was.className = 'price__was';
        amount.insertAdjacentElement('afterend', was);
      }
      was.textContent = money(variant.compare_at_price, fmt);
    } else if (was) {
      was.remove();
    }
  }

  function updateAvailability(variant, form) {
    const btn = form.querySelector('.pdp__add');
    if (btn) {
      btn.disabled = !variant.available;
      const label = btn.querySelector('.btn__label');
      if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }

    const stock = document.querySelector('.pdp__stock');
    if (!stock) return;
    stock.classList.remove('pdp__stock--in', 'pdp__stock--low', 'pdp__stock--out');
    stock.classList.add(variant.available ? 'pdp__stock--in' : 'pdp__stock--out');
    const text = stock.lastChild;
    if (text && text.nodeType === Node.TEXT_NODE) {
      text.textContent = variant.available ? ' In stock' : ' Sold out';
    }
  }

  /* ------------------------------------------------------------ Add to cart */

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;

    e.preventDefault();
    // .pdp__add on the product page, the quick-add button on a card — either way it is
    // this form's submit control.
    const btn = form.querySelector('.pdp__add') || form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-add-status]');
    if (btn) btn.setAttribute('aria-busy', 'true');

    fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.description || body.message || 'Could not add to cart');
        if (status) status.textContent = 'Added to your cart.';
        refreshCartCount();
        openDrawer();
      })
      .catch((err) => {
        // Never strand the shopper: say what happened and leave the form submittable.
        if (status) status.textContent = err.message;
      })
      .finally(() => {
        if (btn) btn.removeAttribute('aria-busy');
      });
  });

  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll('.header__count').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('header__count--empty', cart.item_count === 0);
        });
        document.querySelectorAll('[data-cart-count-sr]').forEach((el) => {
          el.textContent = cart.item_count + ' items in cart';
        });
      })
      .catch(() => {});
  }

  /* ------------------------------------------------------------- Drawer */

  function drawerEl() { return document.querySelector('[data-cart-drawer]'); }

  function openDrawer() {
    // no <dialog> support: nothing opens, and the header cart link still works
    if (!drawerEl() || typeof drawerEl().showModal !== 'function') return;
    // PAGES.md makes this switchable (openOnAdd, default on) — a merchant who turns it
    // off wants the add to be silent, so refresh the contents but do not surface it.
    const host = document.getElementById('cart-drawer-host-inner');
    if (host && host.getAttribute('data-open-on-add') === 'false') {
      refreshDrawer();
      return;
    }
    // Re-query AFTER the refresh — it replaces the dialog node, so a reference taken
    // before would be detached and showModal() on it does nothing at all.
    refreshDrawer().then(() => {
      const d = drawerEl();
      if (d && !d.open) d.showModal();
    });
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]')) {
      const d = drawerEl();
      if (d && d.open) d.close();
    }
  });

  // Clicking the backdrop closes it. <dialog> reports backdrop clicks as clicks on the
  // dialog itself, so the test is whether the point falls outside its box.
  document.addEventListener('click', (e) => {
    const d = drawerEl();
    if (!d || !d.open || e.target !== d) return;
    const r = d.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) d.close();
  });

  // Re-render the drawer from the server rather than patching its nodes: the line
  // totals, the progress bar and the subtotal all move together, and Liquid already
  // knows how to compute them.
  function refreshDrawer() {
    return fetch('/?sections=cart-drawer-section')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data['cart-drawer-section']) return;
        const host = document.getElementById('cart-drawer-host-inner');
        const fresh = new DOMParser()
          .parseFromString(data['cart-drawer-section'], 'text/html')
          .querySelector('#cart-drawer-host-inner');
        if (!host || !fresh) return;
        // Closing first avoids leaving a detached dialog in the top layer, which would
        // keep the page inert with nothing visible to dismiss.
        const wasOpen = drawerEl() && drawerEl().open;
        if (wasOpen) drawerEl().close();
        host.innerHTML = fresh.innerHTML;
        if (wasOpen && drawerEl()) drawerEl().showModal();
      })
      .catch(() => {});
  }

  /* ---------------------------------------------------------------- Cart */

  // Quantity change on a cart line. Debounced because the stepper fires per click and
  // a shopper tapping + four times should cost one request, not four.
  let cartTimer = null;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.qty__input[data-line]');
    if (!input) return;

    clearTimeout(cartTimer);
    cartTimer = setTimeout(() => {
      changeLine(input.getAttribute('data-line'), parseInt(input.value, 10) || 0);
    }, 350);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-remove]');
    if (!btn) return;
    e.preventDefault();
    changeLine(btn.getAttribute('data-line-remove'), 0);
  });

  function changeLine(line, quantity) {
    const form = document.querySelector('[data-cart-form]');
    if (form) form.setAttribute('aria-busy', 'true');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity }),
    })
      .then((r) => r.json())
      .then(() => {
        // Re-render from the server rather than reloading. A full reload is invisible
        // on the cart page but wrong in the drawer — it closes the overlay and throws
        // the shopper back to the top of whatever page they were on, which reads as
        // being kicked out for removing a line.
        //
        // Still not patching nodes by hand: line totals, the progress bar, the subtotal
        // and the free-shipping copy all move together, and Liquid already knows how to
        // compute them. The Section Rendering API gives exactly that, in place.
        refreshCartCount();
        return Promise.all([refreshDrawer(), refreshCartSection()]);
      })
      .catch(() => {})
      .finally(() => {
        const f = document.querySelector('[data-cart-form]');
        if (f) f.removeAttribute('aria-busy');
      });
  }

  // Re-render the cart PAGE section, when one is on screen. The section id is only
  // knowable at runtime (Shopify generates it per template), so it is read back off the
  // wrapper Skeleton emits rather than hardcoded.
  function refreshCartSection() {
    const form = document.querySelector('[data-cart-form]');
    const wrap = form && form.closest('.shopify-section');
    if (!wrap || !wrap.id) return Promise.resolve();

    const id = wrap.id.replace('shopify-section-', '');
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(id))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const html = data && data[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.shopify-section') || doc.body;
        wrap.innerHTML = fresh.innerHTML;
        // The steppers that just came back have no bound bounds state yet.
        wrap.querySelectorAll('[data-qty]').forEach(syncQtyBounds);
      })
      .catch(() => {});
  }

  // With JS on, the no-JS commit buttons are redundant noise.
  document.querySelectorAll('.cart__update').forEach((el) => el.remove());

  /* ---------------------------------------------------------- Video swap */

  // Click-to-load: the iframe is created only when the poster is pressed, so a visitor
  // who never plays it never pays for YouTube's payload or its cookies.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-video-play]');
    if (!btn) return;

    const wrap = btn.closest('[data-video]');
    const id = wrap && wrap.getAttribute('data-video-id');
    if (!id) return;

    const frame = document.createElement('iframe');
    frame.className = 'install__frame';
    // youtube-nocookie + autoplay: the user just asked for it by pressing play.
    frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
    frame.title = btn.getAttribute('aria-label') || 'Video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    frame.allowFullscreen = true;

    btn.replaceWith(frame);
    frame.focus();
  });

  /* ------------------------------------------------- Close drawer on navigate */

  // A <details> drawer stays open across a same-page anchor jump, covering the target.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.header__drawer-link');
    if (!link) return;
    const drawer = link.closest('details');
    if (drawer) drawer.open = false;
  });

  // Escape closes it, matching what every other overlay on the web does.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[scroll-lock][open]').forEach((d) => {
      d.open = false;
      const summary = d.querySelector('summary');
      if (summary) summary.focus();
    });
  });
})();
